/**
 * CurrentQueueNumber Component
 * Large display of current queue number being called
 */

import React, { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Box, Typography, keyframes } from '@mui/material';

// Pulse animation for new number
const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

// Glow animation for new number
const glow = keyframes`
  0% {
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.3);
  }
  50% {
    box-shadow: 0 0 60px rgba(76, 175, 80, 0.8);
  }
  100% {
    box-shadow: 0 0 20px rgba(76, 175, 80, 0.3);
  }
`;

interface CurrentQueueNumberProps {
  number?: number | null;
  name?: string;
}

// Animation store type
interface AnimationStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => boolean;
  trigger: () => void;
}

// Create animation store - manages animation state outside of React's render cycle
function createAnimationStore(): AnimationStore {
  let animate = false;
  const listeners: Set<() => void> = new Set();

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => animate,
    trigger: () => {
      animate = true;
      listeners.forEach((l) => l());
      setTimeout(() => {
        animate = false;
        listeners.forEach((l) => l());
      }, 1500);
    },
  };
}

/**
 * Component to display the current queue number in large format
 */
export const CurrentQueueNumber: React.FC<CurrentQueueNumberProps> = ({ number, name }) => {
  const prevNumberRef = useRef<number | null>(null);

  // Create animation store using useMemo for stable identity across renders
  const animationStore = useMemo(() => createAnimationStore(), []);

  const animate = useSyncExternalStore(animationStore.subscribe, animationStore.getSnapshot);

  // Trigger animation when number changes
  useEffect(() => {
    if (number !== prevNumberRef.current && number !== null && number !== undefined) {
      prevNumberRef.current = number;
      animationStore.trigger();
    }
  }, [number, animationStore]);

  const formatQueueNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) {
      return '---';
    }
    return num.toString().padStart(3, '0');
  };

  return (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 2, md: 3 },
        borderRadius: 4,
        backgroundColor: 'background.paper',
        animation: animate ? `${glow} 1.5s ease-in-out` : 'none',
        flex: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 0,
      }}
    >
      {/* Label */}
      <Typography
        variant="h5"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
          letterSpacing: 4,
          mb: 1,
        }}
      >
        SENHA
      </Typography>

      {/* Queue Number */}
      <Typography
        sx={{
          fontSize: 'clamp(80px, 15vw, 200px)',
          fontWeight: 700,
          lineHeight: 1,
          color: 'primary.main',
          fontFamily: 'monospace',
          animation: animate ? `${pulse} 0.5s ease-in-out` : 'none',
          textShadow: animate ? '0 0 30px rgba(76, 175, 80, 0.5)' : 'none',
          transition: 'text-shadow 0.3s ease',
        }}
      >
        {formatQueueNumber(number)}
      </Typography>

      {/* Patient Name (optional) */}
      {name && (
        <Typography
          variant="h4"
          sx={{
            mt: 1,
            color: 'text.primary',
            fontWeight: 400,
            opacity: 0.9,
          }}
        >
          {name}
        </Typography>
      )}

      {/* Status indicator */}
      {number !== null && number !== undefined && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: 'success.main',
            fontWeight: 500,
          }}
        >
          EM ATENDIMENTO
        </Typography>
      )}
    </Box>
  );
};

export default CurrentQueueNumber;
